// Offline-Warteschlange.
//
// Der eigentliche Grund für dieses Modul: im Supermarkt ist das Netz mies.
// Vorher war die App offline nur lesbar – abhaken ging schlicht nicht.
// Jetzt wird jede Änderung sofort lokal angewandt, in die Outbox gelegt und
// gesendet, sobald es wieder geht. Jede Operation hat eine `opId`, damit ein
// doppelt gesendeter Stapel serverseitig nichts doppelt tut.
import type { ShoppingListResponse, SyncOp, SyncOpKind, SyncResult } from '@speis/shared';
import { ApiError, api } from './api.js';
import { outboxAdd, outboxAll, outboxCount, outboxMarkFailed, outboxRemove } from './idb.js';

/** Nach so vielen vergeblichen Versuchen gilt eine Operation als verloren. */
const MAX_TRIES = 8;

type Listener = (pending: number) => void;
const listeners = new Set<Listener>();
let flushing = false;
let retryTimer: number | null = null;

export function onPendingChange(fn: Listener): () => void {
  listeners.add(fn);
  void outboxCount().then(fn);
  return () => listeners.delete(fn);
}

async function notify(): Promise<void> {
  const count = await outboxCount();
  for (const fn of listeners) fn(count);
}

function newOpId(): string {
  return crypto.randomUUID();
}

export interface QueueResult {
  /** true = direkt beim Server gelandet, false = wartet in der Outbox. */
  online: boolean;
  shopping?: ShoppingListResponse;
}

/**
 * Änderung anwenden: erst direkt versuchen, bei Netzproblemen einreihen.
 * Alles andere (400er, 404er) ist ein echter Fehler und fliegt raus.
 */
export async function queue(
  kind: SyncOpKind,
  payload: Record<string, unknown>,
): Promise<QueueResult> {
  const op: SyncOp = { opId: newOpId(), kind, payload, ts: Date.now() };

  if (navigator.onLine) {
    try {
      const result = await api<SyncResult & { shopping: ShoppingListResponse }>('/api/sync', {
        method: 'POST',
        body: { ops: [op] },
      });
      if (result.failed.length) {
        throw new ApiError(400, result.failed[0]!.error);
      }
      return { online: true, shopping: result.shopping };
    } catch (e) {
      if (!(e instanceof ApiError) || !e.isOffline) throw e;
      // Netz weg zwischen Prüfung und Anfrage – unten einreihen.
    }
  }

  await outboxAdd(op);
  await notify();
  return { online: false };
}

/** Alle wartenden Operationen senden. Läuft nie zweimal gleichzeitig. */
export async function flush(): Promise<SyncResult | null> {
  if (flushing || !navigator.onLine) return null;
  const ops = await outboxAll();
  if (!ops.length) return null;

  flushing = true;
  try {
    // Aufgegebene Operationen nicht ewig mitschleppen.
    const dead = ops.filter((o) => o.tries >= MAX_TRIES).map((o) => o.opId);
    if (dead.length) await outboxRemove(dead);

    const sendable = ops.filter((o) => o.tries < MAX_TRIES);
    if (!sendable.length) {
      await notify();
      return null;
    }

    const result = await api<SyncResult>('/api/sync', {
      method: 'POST',
      body: { ops: sendable.map(({ opId, kind, payload, ts }) => ({ opId, kind, payload, ts })) },
    });

    // Angewandt und übersprungen sind beide erledigt – Letzteres heißt nur,
    // dass der Server sie schon kannte.
    await outboxRemove([...result.applied, ...result.skipped]);
    for (const failure of result.failed) {
      await outboxMarkFailed(failure.opId, failure.error);
    }
    await notify();
    return result;
  } catch (e) {
    if (e instanceof ApiError && e.isOffline) scheduleRetry();
    return null;
  } finally {
    flushing = false;
  }
}

function scheduleRetry(delay = 15_000): void {
  if (retryTimer !== null) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    void flush();
  }, delay);
}

/** Beim Start aufrufen: reagiert auf Netzwechsel und Rückkehr in den Vordergrund. */
export function startOutbox(): void {
  window.addEventListener('online', () => void flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flush();
  });
  void flush();
}

export { outboxCount };
